import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

export type UserRole = 'admin' | 'user';

interface RoleContextType {
  role: UserRole | null;
  isAdmin: boolean;
  isLoading: boolean;
  refreshRole: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshRole = async () => {
    if (!user) {
      setRole(null);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('[Role] Role fetch failed');
        setRole('user');
      } else {
        setRole((data?.role as UserRole) || 'user');
      }
    } catch (error) {
      console.error('[Role] Operation failed');
      setRole('user');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshRole();
  }, [user]);

  const isAdmin = role === 'admin';

  return (
    <RoleContext.Provider value={{ role, isAdmin, isLoading, refreshRole }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};